import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { isEmpty, flatten } from 'lodash';
import { Card } from '../../../ui-kit';
import { IncidentCrewingContainer } from '../../../general/CrewingWarning';
import ElapsedTime from '../IncidentMessage/ElapsedTime';
import { IconArrowUpRightFromSquare } from '../../../ui-kit/icons';

const IncidentListItem = ({
  incident,
  skills,
  displayedIncidentId,
  setDisplayedIncident,
  incidentLocked,
  toggleIncidentLock
}) => {
  const [elapsedTime, setElapsedTime] = useState(null);
  const [timer, setTimer] = useState(null);

  const startTimer = () => {
    setTimer(setInterval(() => updateIncidentElapsedTime(), 1000));
  };

  const updateIncidentElapsedTime = () => {
    setElapsedTime(moment().diff(moment(incident.start_time), 'seconds'));
  };

  useEffect(() => {
    if (elapsedTime) return;

    startTimer();

    return async function cleanup() {
      await clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (incident.start_time) return;

    setTimer(clearInterval(timer));
  }, [incident?.start_time]);

  const enoughConfirmations = useMemo(() => {
    const allSkillStatuses = flatten(incident.warning_statuses.map(ws => ws.skill_statuses));
    return allSkillStatuses.every(skillStatus => skillStatus.level !== 'below_required');
  }, [incident?.warning_statuses]);

  const showCrewingStatus = useMemo(() => {
    // if the incident is 3 seconds old, and still has no responses, we will cut
    // the grace period short.
    if (elapsedTime >= 3 && isEmpty(incident?.incident_responses || [])) return true;

    // return as soon as there are enough confirmations
    if (enoughConfirmations) return true;

    // else, we add a 60s grace period for responses to come in
    if (elapsedTime >= 60) return true;

    return false;
  }, [elapsedTime, incident?.incident_responses]);

  const crewingStatus = useMemo(() => {
    // If we are within the grace period, return grey status (default)
    if (!showCrewingStatus) return 'default';
    // After the grace period, we will return green or red depending
    // on whether there are enough confirmations
    return enoughConfirmations ? 'success' : 'danger';
  }, [elapsedTime, incident?.warning_statuses, showCrewingStatus]);

  const groupId = incident.stations[0]?.id; // get the first group ID of the incident
  const incidentUrl = `/groups/${groupId}/incidents/${incident.id}`;

  // This will be used to override the color of
  const cardColor = {
    success: 'success',
    danger: 'danger'
  }[crewingStatus];

  const isIncidentDisplayed = incident.id === displayedIncidentId;

  const handleIncidentClick = () => {
    if (incident.id !== displayedIncidentId) {
      // When clicking a different incident we always want the switch to take place,
      // regardless of the lock state.
      setDisplayedIncident(incident.id, true);
    }

    // If the incident is not locked, or the incident is locked but has not
    // changed, we toggle to lock.
    if (!incidentLocked || (incidentLocked && incident.id === displayedIncidentId)) toggleIncidentLock();
  };

  return (
    <Card
      className="crewing-warnings-panel"
      bg={cardColor}
      border={isIncidentDisplayed ? 'info' : ''}
      style={{ borderWidth: `${isIncidentDisplayed ? '6px' : ''}` }}
      onClick={handleIncidentClick}
    >
      <Card.Header>
        <ElapsedTime startTime={incident.start_time} />{' '}
        <h4 className="m-0">
          <a style={{ color: 'var(--bs-body-color)' }} href={incidentUrl} target="_blank" rel="noreferrer">
            {incident.body} <IconArrowUpRightFromSquare />
          </a>
        </h4>
      </Card.Header>
      <Card.Body>
        <IncidentCrewingContainer
          incident={incident}
          skills={skills}
          showAllStatuses={true}
          hideHeader={true}
          onlyShowUnderstaffed={true}
          className="condensed"
        />
      </Card.Body>
    </Card>
  );
};

export default IncidentListItem;
